import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Plus, Search, Eye, Download, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DriverList() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      const response = await api.get('/drivers');
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to load drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/drivers?search=${searchTerm}`);
      setDrivers(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/drivers-download/csv`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'drivers_report.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Drivers CSV downloaded');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download CSV');
    } finally {
      setDownloading(false);
    }
  };

  const handleQuickApprove = async (driverId) => {
    setActionLoading((prev) => ({ ...prev, [driverId]: true }));
    try {
      await api.patch(`/drivers/${driverId}/status`, { status: 'active' });
      toast.success(`Driver ${driverId} approved`);
      loadDrivers();
    } catch (error) {
      toast.error('Failed to approve driver');
    } finally {
      setActionLoading((prev) => ({ ...prev, [driverId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shizah-blue"></div>
      </div>
    );
  }

  return (
    <div data-testid="drivers-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">Drivers</h1>
          <p className="text-gray-600">Manage your driver database</p>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid="download-drivers-csv"
            onClick={handleDownloadCSV}
            disabled={downloading}
            variant="outline"
            className="border-shizah-blue text-shizah-blue hover:bg-blue-50"
          >
            <Download size={20} className="mr-2" />
            {downloading ? 'Downloading...' : 'Download CSV'}
          </Button>
          <Link to="/drivers/new">
            <Button data-testid="add-driver-button" className="bg-shizah-blue hover:bg-blue-700 text-white">
              <Plus size={20} className="mr-2" />
              Add Driver
            </Button>
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex gap-3">
          <Input
            data-testid="driver-search-input"
            placeholder="Search by ID, name, or mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1"
          />
          <Button data-testid="search-button" onClick={handleSearch} className="bg-shizah-blue hover:bg-blue-700 text-white">
            <Search size={20} className="mr-2" />
            Search
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="drivers-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Driver ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Mobile
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {drivers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No drivers found. Add your first driver to get started.
                  </td>
                </tr>
              ) : (
                drivers.map((driver) => (
                  <tr key={driver.driver_id} className="hover:bg-gray-50" data-testid={`driver-row-${driver.driver_id}`}>
                    <td className="px-6 py-4 text-sm">
                      <Link
                        to={`/drivers/${driver.driver_id}`}
                        className="font-semibold text-shizah-blue hover:underline"
                        data-testid={`driver-link-${driver.driver_id}`}
                      >
                        {driver.driver_id}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{driver.full_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{driver.mobile}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{driver.location}</td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          driver.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : driver.status === 'pending_approval'
                            ? 'bg-yellow-100 text-yellow-800'
                            : driver.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {driver.status === 'pending_approval' ? 'Pending' : driver.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Link to={`/drivers/${driver.driver_id}`}>
                          <Button
                            data-testid={`view-driver-${driver.driver_id}`}
                            variant="ghost"
                            size="sm"
                            className="text-shizah-blue hover:bg-blue-50"
                          >
                            <Eye size={16} className="mr-1" />
                            View
                          </Button>
                        </Link>
                        {driver.status === 'pending_approval' && (
                          <Button
                            data-testid={`approve-driver-${driver.driver_id}`}
                            size="sm"
                            disabled={actionLoading[driver.driver_id]}
                            onClick={() => handleQuickApprove(driver.driver_id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <CheckCircle size={14} className="mr-1" />
                            Approve
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}